import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarCheck, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, CloudSun, Users } from "lucide-react";

// Tomorrow Ready — one collapsible card on Home/My Day. Uses existing schedule,
// weather, tasks and crew-card data. Never a new nav tab.
export default function TomorrowReady() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/tomorrow-ready").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data || data.dismissed) return null;
  const issues = data.issues || [];
  const count = issues.length;

  const markReady = async () => {
    setBusy(true);
    try {
      await api.post("/tomorrow-ready/mark-ready", { date: data.date });
      setData((d) => ({ ...d, dismissed: true }));
    } catch (e) { /* retry on next load */ }
    setBusy(false);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="tomorrow-ready">
      <button onClick={() => setOpen((v) => !v)} data-testid="tr-toggle" aria-expanded={open}
        className="flex w-full items-center gap-3 p-4 text-left min-h-[56px]">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${count ? "bg-amber-500/15 text-amber-600" : "bg-green-500/15 text-green-600"}`}>
          <CalendarCheck className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-extrabold text-slate-900">{t("tr_title")}</span>
          <span className={`block text-xs font-semibold ${count ? "text-amber-600" : "text-green-600"}`} data-testid="tr-status">
            {data.jobs.length === 0 ? t("tr_no_jobs") : count === 0 ? t("tr_ready") : t("tr_issues", { count })}
          </span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3" data-testid="tr-body">
          {(data.jobs || []).map((j) => (
            <button key={j.project_id} onClick={() => navigate(`/app/projects/${j.project_id}`)}
              data-testid={`tr-job-${j.project_id}`}
              className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-2.5 text-left active:scale-[0.99]">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-slate-900">{j.project_name}</span>
                <span className="flex items-center gap-2 text-[11px] text-slate-500">
                  <Users className="h-3 w-3" /> {j.crew.length ? j.crew.join(", ") : "—"}
                  {j.start && <span>· {j.start}</span>}
                </span>
              </span>
              {j.weather && (
                <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
                  <CloudSun className="h-3.5 w-3.5" /> {j.weather}
                </span>
              )}
            </button>
          ))}
          {issues.length > 0 && (
            <ul className="space-y-1.5" data-testid="tr-issues">
              {issues.map((iss, i) => (
                <li key={i} className="flex items-center gap-2 text-xs font-semibold text-amber-700" data-testid={`tr-issue-${i}`}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t(`tr_iss_${iss.key}`, { project: iss.project || "", detail: iss.detail || "", count: iss.count || 0 })}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {issues.length > 0 && (
              <button onClick={() => navigate("/app/schedule")} data-testid="tr-review"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-amber-600 px-4 text-xs font-bold text-white active:scale-95">
                <AlertTriangle className="h-4 w-4" /> {t("tr_review")}
              </button>
            )}
            {issues.some((i) => i.key === "missing_crew_card") && (
              <button onClick={() => navigate("/app/projects")} data-testid="tr-send-cards"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-brand/40 bg-brand/5 px-4 text-xs font-bold text-brand active:scale-95">
                {t("tr_send_cards")}
              </button>
            )}
            <button onClick={markReady} disabled={busy} data-testid="tr-mark-ready"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-green-600 px-4 text-xs font-bold text-white active:scale-95 disabled:opacity-60">
              <CheckCircle2 className="h-4 w-4" /> {t("tr_mark_ready")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
